// Orquestación única de la impresión de un comprobante ya emitido
// (FE-RTK-004, D5 del PLAN `reimpresion-tickets-plan.md`).
//
// `maybePrintInvoice` está hoy duplicado con variaciones en PantallaInicio,
// CobroFacturaVentaView, CobroVentaRapidaView y GestionPedidos. Agregar una
// quinta copia para el histórico garantizaba que divergieran. Esto resuelve
// settings, confirmación y mensajes en un solo lugar; las pantallas sólo
// invocan y muestran.
import localPrintSettingsService from './localPrintSettingsService';
import invoiceLocalPrintService from './invoiceLocalPrintService';

// Estados en los que un comprobante se puede reimprimir. Es lista BLANCA, no
// negra: un estado nuevo que nadie contempló no debería volverse imprimible
// por omisión (R-4 del PLAN).
const ESTADOS_IMPRIMIBLES = new Set(['vigente', 'pendiente', 'emitido', 'confirmado']);

export function puedeReimprimirEstado(estado) {
  return ESTADOS_IMPRIMIBLES.has(String(estado || '').trim().toLowerCase());
}

// ¿La impresión local está disponible en este puesto? Si no, el botón no se
// muestra: ofrecer imprimir donde la impresión está apagada es prometer algo
// que no va a pasar (D8).
export function impresionLocalHabilitada() {
  const settings = localPrintSettingsService.getSettings();
  return Boolean(settings?.enabled) && settings?.invoicePrintMode !== 'disabled';
}

/**
 * Reimprime el comprobante de una venta ya emitida.
 *
 * No re-emite nada fiscalmente ni toca `ventas`: vuelve a mandar a papel un
 * documento que ya existe.
 *
 * @returns {Promise<{ok: boolean, omitido?: boolean, duplicate?: boolean, mode?: string}>}
 */
export async function reimprimirComprobante({ saleId, movimientoId = null, invoiceNumber = '' }) {
  const settings = localPrintSettingsService.getSettings();

  if (!settings?.enabled || settings.invoicePrintMode === 'disabled') {
    return { ok: false, omitido: true, motivo: 'impresion_deshabilitada' };
  }

  if (settings.invoicePrintMode === 'confirm') {
    const confirmado = await window.confirm(
      `¿Enviar el ticket ${invoiceNumber || `de la venta #${saleId}`} a la impresora local?`,
      { title: 'Reimprimir ticket', confirmText: 'Imprimir', cancelText: 'Cancelar' }
    );
    if (!confirmado) return { ok: false, omitido: true, motivo: 'cancelado_por_usuario' };
  }

  // `printJob` va en null a propósito: en una reimpresión no hay respuesta de
  // cobro de la cual reaprovechar el ticket, así que se pide al backend.
  const resultado = await invoiceLocalPrintService.printInvoiceBySale({
    saleId,
    movimientoId,
    settings
  });

  return { ok: true, ...resultado };
}

// Traduce el resultado a un mensaje para el operador. Vive acá para que las
// cuatro pantallas digan lo mismo ante lo mismo.
export function mensajeResultadoReimpresion(resultado, invoiceNumber = '') {
  const ref = invoiceNumber ? ` ${invoiceNumber}` : '';
  if (resultado?.duplicate) {
    return { tipo: 'info', texto: `El ticket${ref} ya se había enviado recién; no se imprimió de nuevo.` };
  }
  if (resultado?.mode === 'offline_contingency') {
    return { tipo: 'info', texto: 'Comprobante temporal enviado. Queda pendiente de validación fiscal.' };
  }
  return { tipo: 'success', texto: `Ticket${ref} enviado a la impresora local.` };
}

export default {
  puedeReimprimirEstado,
  impresionLocalHabilitada,
  reimprimirComprobante,
  mensajeResultadoReimpresion
};
